import { useMemo, useState } from "react";
import MediaCard from "./MediaCard";
import Pagination from "./Pagination";
import { uniqueValues } from "@/lib/content";
import type { Movie, TvShow } from "@/types/media";

type CatalogItem = Movie | TvShow;
type SortKey = "new" | "rating" | "popularity";

const PAGE_SIZE = 8;
const ALL = "全部";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "new", label: "最新" },
  { key: "rating", label: "评分" },
  { key: "popularity", label: "热门" },
];

function pillClass(active: boolean) {
  return `cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-label transition-colors duration-200 ${
    active
      ? "bg-primary-500 text-background-50"
      : "bg-background-200/70 text-foreground-600 hover:bg-background-300/70 hover:text-foreground-950"
  }`;
}

interface PillGroupProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

function PillGroup({ label, value, options, onChange }: PillGroupProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <span className="w-12 shrink-0 pt-1 text-xs text-foreground-500">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onChange(ALL)} className={pillClass(value === ALL)}>
          全部
        </button>
        {options.map((option) => (
          <button key={option} type="button" onClick={() => onChange(option)} className={pillClass(value === option)}>
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

interface CatalogViewProps {
  items: CatalogItem[];
  emptyText: string;
}

export default function CatalogView({ items, emptyText }: CatalogViewProps) {
  const [genre, setGenre] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [year, setYear] = useState(ALL);
  const [sort, setSort] = useState<SortKey>("new");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const genres = useMemo(() => uniqueValues(items.flatMap((item) => item.genres)), [items]);
  const regions = useMemo(() => uniqueValues(items.map((item) => item.region)), [items]);
  const years = useMemo(
    () => uniqueValues(items.map((item) => String(item.year))).sort((a, b) => Number(b) - Number(a)),
    [items]
  );

  const filtered = useMemo(() => {
    const list = items.filter((item) => {
      if (genre !== ALL && !item.genres.includes(genre)) return false;
      if (region !== ALL && item.region !== region) return false;
      if (year !== ALL && String(item.year) !== year) return false;
      return true;
    });
    list.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "popularity") return b.popularity - a.popularity;
      return b.year - a.year;
    });
    return list;
  }, [items, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const applyChange = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setGenre(ALL);
    setRegion(ALL);
    setYear(ALL);
    setSort("new");
    setPage(1);
  };

  const hasFilter = genre !== ALL || region !== ALL || year !== ALL;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-4 md:p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-filter-3-line text-primary-400" />
            <span>按类型、地区、年份筛选</span>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((value) => !value)}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-1.5 text-xs text-foreground-700 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400 lg:hidden"
          >
            <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            {showFilters ? "收起筛选" : "展开筛选"}
          </button>
        </div>

        <div className={`${showFilters ? "flex" : "hidden"} mt-4 flex-col gap-4 lg:flex`}>
          <PillGroup label="类型" value={genre} options={genres} onChange={applyChange(setGenre)} />
          <PillGroup label="地区" value={region} options={regions} onChange={applyChange(setRegion)} />
          <PillGroup label="年份" value={year} options={years} onChange={applyChange(setYear)} />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <span className="w-12 shrink-0 pt-1 text-xs text-foreground-500">排序</span>
            <div className="flex flex-wrap items-center gap-2">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSort(option.key);
                    setPage(1);
                  }}
                  className={pillClass(sort === option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-background-200/70 pt-4">
            <span className="text-xs text-foreground-500">共 {filtered.length} 部作品</span>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex cursor-pointer items-center gap-1 text-xs text-primary-400 transition-colors duration-200 hover:text-primary-300"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            )}
          </div>
        </div>
      </div>

      {paged.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {paged.map((item) => (
            <MediaCard
              key={item.id}
              id={item.id}
              title={item.title}
              year={item.year}
              cover={item.cover}
              summary={item.summary}
              tags={[item.region, ...item.genres]}
              rating={item.rating}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-2xl text-foreground-500">
            <i className="ri-emotion-sad-line" />
          </span>
          <p className="mt-4 text-sm text-foreground-700">{emptyText}</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      )}

      <Pagination current={current} total={totalPages} onChange={setPage} />
    </div>
  );
}